import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { LogKayitlari } from '../types/logKayitlari';
import { toast } from 'react-toastify';
import TrDateInput from '../components/TrDateInput';
import { useTranslation } from 'react-i18next';

const IslemKayitlari: React.FC = () => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState<LogKayitlari[]>([]);
    const [filteredLogs, setFilteredLogs] = useState<LogKayitlari[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState<LogKayitlari | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    // Filtre states
    const [filters, setFilters] = useState({
        kullaniciAdi: '',
        islemTipi: '',
        baslangicTarihi: '',
        bitisTarihi: '',
        takipNo: '',
        basariliMi: 'tumu',
        endpoint: ''
    });

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [logs, filters]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/LogKayitlari');
            setLogs(response.data);
        } catch (error) {
            console.error('Log kayıtları yüklenirken hata:', error);
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...logs];

        // Kullanıcı adı filtresi
        if (filters.kullaniciAdi) {
            filtered = filtered.filter(log =>
                log.kullanici?.adSoyad?.toLowerCase().includes(filters.kullaniciAdi.toLowerCase())
            );
        }

        // İşlem tipi filtresi
        if (filters.islemTipi) {
            filtered = filtered.filter(log => log.islemTipi === filters.islemTipi);
        }

        // Takip No filtresi
        if (filters.takipNo) {
            filtered = filtered.filter(log =>
                log.takipNo?.toLowerCase().includes(filters.takipNo.toLowerCase())
            );
        }

        // Endpoint filtresi
        if (filters.endpoint) {
            filtered = filtered.filter(log =>
                log.endpoint?.toLowerCase().includes(filters.endpoint.toLowerCase())
            );
        }

        // Başarı durumu filtresi
        if (filters.basariliMi !== 'tumu') {
            filtered = filtered.filter(log =>
                log.basariliMi === (filters.basariliMi === 'basarili')
            );
        }

        // Tarih filtresi
        if (filters.baslangicTarihi) {
            filtered = filtered.filter(log =>
                new Date(log.islemTarihi) >= new Date(filters.baslangicTarihi)
            );
        }

        if (filters.bitisTarihi) {
            const bitisTarihiSonrasi = new Date(filters.bitisTarihi);
            bitisTarihiSonrasi.setHours(23, 59, 59, 999); // Günün sonunu dahil et
            filtered = filtered.filter(log =>
                new Date(log.islemTarihi) <= bitisTarihiSonrasi
            );
        }

        setFilteredLogs(filtered);
    };

    const handleFilterChange = (field: string, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            kullaniciAdi: '',
            islemTipi: '',
            baslangicTarihi: '',
            bitisTarihi: '',
            takipNo: '',
            basariliMi: 'tumu',
            endpoint: ''
        });
        setCurrentPage(1);
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
        } catch {
            return dateString;
        }
    };

    const getIslemBadgeClass = (islemTipi?: string) => {
        switch (islemTipi) {
            case 'Create': return 'badge-create';
            case 'Update': return 'badge-update';
            case 'Delete': return 'badge-delete';
            case 'Login': return 'badge-login';
            default: return 'badge-default';
        }
    };

    const getEndpointLabel = (endpoint?: string): string => {
        if (!endpoint) return '-';
        const lower = endpoint.toLowerCase();
        if (lower.includes('/auth/login')) return t('islem.sistemGirisi');
        if (lower.includes('/leadactivities')) return t('islem.potansiyelMusteri');
        if (lower.includes('/leads')) return t('islem.leadKaydi');
        if (lower.includes('/cariler')) {
            if (lower.includes('/update')) return t('islem.cariGuncelleme');
            return t('islem.cariIslemi');
        }
        if (lower.includes('/projeler')) return t('islem.projeIslemi');
        if (lower.includes('/daireler')) return t('islem.daireIslemi');
        if (lower.includes('/bloklar')) return 'Blok İşlemi';
        if (lower.includes('/katlar')) return 'Kat İşlemi';
        if (lower.includes('/odemeler')) return t('islem.odemeIslemi');
        if (lower.includes('/kasahareketleri')) return 'Kasa Hareketi';
        if (lower.includes('/kasalar')) return t('islem.kasaIslemi');
        if (lower.includes('/kullanicilar')) return t('islem.kullaniciIslemi');
        if (lower.includes('/sozlesmeler')) return t('islem.sozlesmeIslemi');
        if (lower.includes('/senetler')) return 'Senet İşlemi';
        if (lower.includes('/dosyalar')) return 'Dosya İşlemi';
        if (lower.includes('/logkayitlari')) return 'Log Sorgusu';
        if (lower.includes('/isplanlari')) return 'İş Planı İşlemi';
        if (lower.includes('/finanskategorileri')) return 'Finans Kategori İşlemi';
        if (lower.includes('/notlar') || lower.includes('/kullanicinotlari')) return 'Not İşlemi';
        if (lower.includes('/manzaralar')) return 'Manzara İşlemi';
        if (lower.includes('/cepheler')) return 'Cephe İşlemi';
        if (lower.includes('/dairetipleri')) return 'Daire Tipi İşlemi';
        if (lower.includes('/parabirimler')) return 'Para Birimi İşlemi';
        if (lower.includes('/dovizkuru')) return 'Döviz Kuru İşlemi';
        if (lower.includes('/kurumsal')) return 'Kurumsal İşlem';
        return endpoint;
    };

    const getUserInitials = (name: string): string => {
        const parts = name.trim().split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return name[0]?.toUpperCase() || '?';
    };

    const exportCSV = () => {
        const headers = ['Tarih', 'Kullanıcı', 'İşlem Tipi', 'Detay', 'Takip No', 'IP Adresi', 'Durum'];
        const rows = filteredLogs.map(log => [
            formatDate(log.islemTarihi),
            log.kullanici?.adSoyad || 'Bilinmeyen',
            log.islemTipi || '-',
            getEndpointLabel(log.endpoint),
            log.takipNo || '-',
            log.ipAdresi || '-',
            log.basariliMi ? 'Başarılı' : 'Hatalı'
        ]);
        const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `islem-kayitlari-${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const showLogDetail = (log: LogKayitlari) => {
        setSelectedLog(log);
        setShowDetailModal(true);
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedLog(null);
    };

    const uniqueIslemTipleri = Array.from(new Set(logs.map(l => l.islemTipi).filter(Boolean)));

    const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
    const paginatedLogs = filteredLogs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (
        <>
            <style>{`
                .log-page {
                    background: #f8f9fa;
                    min-height: 100vh;
                    font-family: 'Inter', 'Segoe UI', 'Roboto', system-ui, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    padding: 24px 32px;
                    color: #334155;
                }
                .log-page * { box-sizing: border-box; }

                /* --- Header --- */
                .log-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                }
                .log-header-left {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                }
                .log-header-icon {
                    width: 42px;
                    height: 42px;
                    background: #fff3e6;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #cd6906;
                    font-size: 1.1rem;
                }
                .log-header h1 {
                    font-size: 1.35rem;
                    font-weight: 700;
                    color: #0f172a;
                    margin: 0;
                    letter-spacing: -0.01em;
                }
                .log-header-right {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .log-record-badge {
                    background: #f1f5f9;
                    color: #64748b;
                    font-size: 0.75rem;
                    font-weight: 600;
                    padding: 4px 12px;
                    border-radius: 20px;
                }
                .log-header-sub {
                    font-size: 0.78rem;
                    color: #94a3b8;
                    margin: 2px 0 0;
                }
                .log-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 9px 20px;
                    background: #fff;
                    color: #cd6906;
                    border: 1px solid #cd6906;
                    border-radius: 8px;
                    font-size: 0.84rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .log-btn-primary:hover {
                    background: #cd6906;
                    color: #fff;
                }
                .log-btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .log-btn-csv {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    padding: 9px 20px;
                    background: #fff;
                    color: #374151;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 0.84rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .log-btn-csv:hover {
                    background: #f9fafb;
                    border-color: #d1d5db;
                }
                .log-btn-apply {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 8px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s;
                    align-self: flex-end;
                    white-space: nowrap;
                    height: 38px;
                }
                .log-btn-apply:hover {
                    background: #b35d05;
                }
                .log-user-cell {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .log-user-avatar {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: #cd6906;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .log-pagination {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 20px;
                    border-top: 1px solid #f1f5f9;
                    font-size: 0.82rem;
                    color: #64748b;
                }
                .log-pagination-left {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .log-pagination-left select {
                    padding: 4px 8px;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    color: #334155;
                    background: #fff;
                    outline: none;
                    cursor: pointer;
                }
                .log-pagination-left select:focus {
                    border-color: #111827;
                }
                .log-pagination-right {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .log-page-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 1px solid #e2e8f0;
                    background: #fff;
                    color: #64748b;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    transition: all 0.15s;
                }
                .log-page-btn:hover:not(:disabled) {
                    border-color: #111827;
                    color: #cd6906;
                }
                .log-page-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }
                .log-page-info {
                    font-size: 0.8rem;
                    color: #64748b;
                    padding: 0 8px;
                }

                /* --- Filter Panel --- */
                .log-filter-panel {
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    margin-bottom: 20px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                    overflow: hidden;
                }
                .log-filter-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 20px;
                    cursor: pointer;
                    transition: background 0.15s;
                }
                .log-filter-header:hover {
                    background: #fafbfc;
                }
                .log-filter-header h3 {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: #334155;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .log-filter-header h3 i { color: #cd6906; font-size: 0.8rem; }
                .log-filter-toggle {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 0.8rem;
                    padding: 4px 8px;
                    border-radius: 4px;
                    transition: color 0.15s;
                }
                .log-filter-toggle:hover { color: #cd6906; }
                .log-filter-body {
                    padding: 0 20px 20px;
                    border-top: 1px solid #f1f5f9;
                }
                .log-filter-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 14px;
                    padding-top: 16px;
                }
                .log-form-group { display: flex; flex-direction: column; gap: 5px; }
                .log-form-label {
                    font-size: 0.7rem;
                    font-weight: 600;
                    color: #94a3b8;
                    letter-spacing: 0.5px;
                }
                .log-form-input,
                .log-form-select {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.82rem;
                    color: #0f172a;
                    background: #fff;
                    outline: none;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    font-family: inherit;
                    height: 38px;
                }
                .log-form-input:focus,
                .log-form-select:focus {
                    border-color: #111827;
                    box-shadow: 0 0 0 3px rgba(205,105,6,0.1);
                }
                .log-form-input::placeholder { color: #cbd5e1; }
                .log-btn-clear {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 8px 16px;
                    background: #fff;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.15s;
                    align-self: flex-end;
                    white-space: nowrap;
                    height: 38px;
                }
                .log-btn-clear:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                    color: #334155;
                }

                /* --- Table Card --- */
                .log-table-card {
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                    overflow: hidden;
                }
                .log-table-card-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 14px 20px;
                    border-bottom: 1px solid #f1f5f9;
                }
                .log-result-count {
                    font-size: 0.82rem;
                    color: #64748b;
                    font-weight: 500;
                }
                .log-result-count strong {
                    color: #cd6906;
                    font-weight: 700;
                }

                /* --- Table --- */
                .log-table-wrapper {
                    overflow-x: auto;
                    max-height: 620px;
                    overflow-y: auto;
                }
                .log-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .log-table .table-header th {
                    background: rgba(248,250,252,0.8);
                    color: #94a3b8;
                    font-size: 0.68rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    padding: 10px 16px;
                    border-bottom: 1px solid #e2e8f0;
                    white-space: nowrap;
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    text-align: left;
                }
                .log-row {
                    background: #fff;
                    transition: all 0.15s;
                    cursor: default;
                }
                .log-row:hover {
                    background: #f3f4f6;
                    box-shadow: inset 4px 0 0 0 #cd6906;
                }
                .log-row td {
                    padding: 12px 16px;
                    border-bottom: 1px solid #f1f5f9;
                    color: #334155;
                    font-size: 0.82rem;
                    vertical-align: middle;
                    white-space: nowrap;
                }

                /* --- Badges --- */
                .log-badge {
                    padding: 2px 10px;
                    border-radius: 100px;
                    font-size: 12px;
                    font-weight: 600;
                    display: inline-block;
                }
                .badge-create {
                    background: #dcfce7;
                    color: #16a34a;
                }
                .badge-update {
                    background: #fef9c3;
                    color: #ca8a04;
                }
                .badge-delete {
                    background: #fef2f2;
                    color: #dc2626;
                }
                .badge-login {
                    background: #dbeafe;
                    color: #2563eb;
                }
                .badge-default {
                    background: #f1f5f9;
                    color: #64748b;
                }
                .badge-endpoint {
                    font-size: 0.82rem;
                    color: #334155;
                    font-weight: 500;
                }
                .badge-success-status {
                    background: #ecfdf5;
                    color: #065f46;
                    padding: 3px 10px;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }
                .badge-success-status::before {
                    content: '';
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #10b981;
                    display: inline-block;
                }
                .badge-fail-status {
                    background: #fef2f2;
                    color: #991b1b;
                    padding: 3px 10px;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                }
                .badge-fail-status::before {
                    content: '';
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #ef4444;
                    display: inline-block;
                }

                /* --- Detail Button --- */
                .log-detail-btn {
                    width: 30px;
                    height: 30px;
                    border-radius: 6px;
                    border: 1px solid #e2e8f0;
                    background: #fff;
                    color: #cd6906;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.75rem;
                    transition: all 0.15s;
                }
                .log-detail-btn:hover {
                    background: #cd6906;
                    border-color: #111827;
                    color: #fff;
                }

                /* --- Empty & Loading --- */
                .log-empty {
                    text-align: center;
                    padding: 56px 20px;
                    color: #94a3b8;
                }
                .log-empty i {
                    font-size: 1.8rem;
                    display: block;
                    margin-bottom: 12px;
                    color: #cbd5e1;
                }
                .log-empty p {
                    margin: 0;
                    font-size: 0.85rem;
                }
                .log-loading {
                    text-align: center;
                    padding: 60px 20px;
                }
                .log-spinner {
                    width: 36px;
                    height: 36px;
                    border: 3px solid #e2e8f0;
                    border-top-color: #cd6906;
                    border-radius: 50%;
                    animation: log-spin 0.7s linear infinite;
                }
                @keyframes log-spin {
                    to { transform: rotate(360deg); }
                }

                /* --- Modal --- */
                .log-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(15,23,42,0.4);
                    backdrop-filter: blur(3px);
                    -webkit-backdrop-filter: blur(3px);
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    z-index: 1050;
                    padding: 50px 16px 16px;
                    overflow-y: auto;
                }
                .log-modal {
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    width: 100%;
                    max-width: 900px;
                    box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 0 1px rgba(0,0,0,0.1);
                    animation: log-modal-in 0.2s ease-out;
                    color: #334155;
                }
                @keyframes log-modal-in {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .log-modal-header {
                    padding: 18px 24px;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .log-modal-header h5 {
                    color: #0f172a;
                    font-size: 0.95rem;
                    font-weight: 600;
                    margin: 0;
                }
                .log-modal-close {
                    background: none;
                    border: none;
                    color: #94a3b8;
                    font-size: 1.2rem;
                    cursor: pointer;
                    width: 30px;
                    height: 30px;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s;
                }
                .log-modal-close:hover {
                    background: #f1f5f9;
                    color: #0f172a;
                }
                .log-modal-body {
                    padding: 20px 24px;
                }
                .log-modal-footer {
                    padding: 14px 24px;
                    border-top: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: flex-end;
                }
                .log-btn-close {
                    padding: 8px 18px;
                    border-radius: 6px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    border: 1px solid #e2e8f0;
                    background: #fff;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .log-btn-close:hover {
                    background: #f8fafc;
                    color: #334155;
                    border-color: #cbd5e1;
                }

                /* --- Modal Detail Grid --- */
                .log-detail-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 24px;
                }
                .log-detail-section h6 {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #cd6906;
                    letter-spacing: 0.5px;
                    margin-bottom: 12px;
                    padding-bottom: 8px;
                    border-bottom: 2px solid #fff3e6;
                }
                .log-detail-table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .log-detail-table td {
                    padding: 8px 12px;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.82rem;
                }
                .log-detail-table .dt-label {
                    color: #94a3b8;
                    font-weight: 500;
                    width: 130px;
                    white-space: nowrap;
                }
                .log-detail-table .dt-value {
                    color: #0f172a;
                }

                /* --- Code Block --- */
                .log-code-block {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    padding: 14px;
                    max-height: 200px;
                    overflow: auto;
                    font-family: 'JetBrains Mono', 'SF Mono', 'Consolas', monospace;
                    font-size: 0.72rem;
                    color: #475569;
                    white-space: pre-wrap;
                    word-break: break-all;
                    margin: 0;
                }
                .log-error-block {
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 14px;
                    margin-top: 16px;
                }
                .log-error-block h6 {
                    color: #991b1b;
                    font-size: 0.82rem;
                    font-weight: 600;
                    margin: 0 0 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .log-data-section {
                    margin-top: 16px;
                }
                .log-data-section h6 {
                    font-size: 0.78rem;
                    font-weight: 600;
                    color: #334155;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .log-data-section h6 i.text-req { color: #3b82f6; }
                .log-data-section h6 i.text-res { color: #10b981; }

                /* --- Responsive --- */
                @media (max-width: 768px) {
                    .log-page { padding: 16px; }
                    .log-header { flex-direction: column; align-items: flex-start; gap: 12px; }
                    .log-filter-grid { grid-template-columns: 1fr; }
                    .log-detail-grid { grid-template-columns: 1fr; }
                    .log-modal { max-width: 100%; }
                }
            `}</style>

            <div className="log-page">
                {/* Header */}
                <div className="log-header">
                    <div className="log-header-left">
                        <div className="log-header-icon"><i className="fas fa-history"></i></div>
                        <div>
                            <h1>{t('islem.baslik')}</h1>
                            <p className="log-header-sub">Sistem işlem geçmişi ve denetim kayıtları</p>
                        </div>
                    </div>
                    <div className="log-header-right">
                        <span className="log-record-badge">{filteredLogs.length} kayıt</span>
                        <button className="log-btn-csv" onClick={exportCSV}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="16" height="16"><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                            CSV İndir
                        </button>
                        <button className="log-btn-primary" onClick={fetchLogs} disabled={loading}>
                            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Yenile
                        </button>
                    </div>
                </div>

                {/* Filtreler */}
                <div className="log-filter-panel">
                    <div className="log-filter-header" onClick={() => setShowFilters(!showFilters)}>
                        <h3><i className="fas fa-filter"></i> Filtreler</h3>
                        <button className="log-filter-toggle">
                            <i className={`fas fa-chevron-${showFilters ? 'up' : 'down'}`}></i>
                        </button>
                    </div>
                    {showFilters && (
                        <div className="log-filter-body">
                            <div className="log-filter-grid">
                                <div className="log-form-group">
                                    <label className="log-form-label">Kullanıcı Adı</label>
                                    <input
                                        className="log-form-input"
                                        placeholder={t('label.ara')}
                                        value={filters.kullaniciAdi}
                                        onChange={(e) => handleFilterChange('kullaniciAdi', e.target.value)}
                                    />
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">İşlem Tipi</label>
                                    <select
                                        className="log-form-select"
                                        value={filters.islemTipi}
                                        onChange={(e) => handleFilterChange('islemTipi', e.target.value)}
                                    >
                                        <option value="">{t('label.tumu')}</option>
                                        {uniqueIslemTipleri.map(tip => (
                                            <option key={tip} value={tip}>{tip}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">Başlangıç Tarihi</label>
                                    <TrDateInput
                                        className="log-form-input"
                                        value={filters.baslangicTarihi}
                                        onChange={(e) => handleFilterChange('baslangicTarihi', e.target.value)}
                                    />
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">Bitiş Tarihi</label>
                                    <TrDateInput
                                        className="log-form-input"
                                        value={filters.bitisTarihi}
                                        onChange={(e) => handleFilterChange('bitisTarihi', e.target.value)}
                                    />
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">Takip No</label>
                                    <input
                                        className="log-form-input"
                                        placeholder="Takip No ara..."
                                        value={filters.takipNo}
                                        onChange={(e) => handleFilterChange('takipNo', e.target.value)}
                                    />
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">Endpoint</label>
                                    <input
                                        className="log-form-input"
                                        placeholder="Endpoint ara..."
                                        value={filters.endpoint}
                                        onChange={(e) => handleFilterChange('endpoint', e.target.value)}
                                    />
                                </div>
                                <div className="log-form-group">
                                    <label className="log-form-label">Durum</label>
                                    <select
                                        className="log-form-select"
                                        value={filters.basariliMi}
                                        onChange={(e) => handleFilterChange('basariliMi', e.target.value)}
                                    >
                                        <option value="tumu">Tümü</option>
                                        <option value="basarili">Başarılı</option>
                                        <option value="basarisiz">Başarısız</option>
                                    </select>
                                </div>
                                <div className="log-form-group">
                                    <button className="log-btn-apply" onClick={applyFilters}>
                                        <i className="fas fa-check"></i> Uygula
                                    </button>
                                </div>
                                <div className="log-form-group">
                                    <button className="log-btn-clear" onClick={clearFilters}>
                                        <i className="fas fa-times"></i> Filtreleri Temizle
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Table Card */}
                <div className="log-table-card">
                    <div className="log-table-card-header">
                        <span className="log-result-count">
                            Toplam <strong>{filteredLogs.length}</strong> kayıt
                        </span>
                    </div>
                    <div className="log-table-wrapper">
                    {loading ? (
                        <div className="log-loading">
                            <div className="log-spinner" style={{ margin: '0 auto' }}></div>
                        </div>
                    ) : (
                        <table className="log-table">
                            <thead>
                                <tr className="table-header">
                                    <th>{t('label.tarih')}</th>
                                    <th>{t('label.kullanici')}</th>
                                    <th>İşlem Tipi</th>
                                    <th>Detay</th>
                                    <th>Takip No</th>
                                    <th>IP Adresi</th>
                                    <th>{t('label.durum')}</th>
                                    <th>Detay</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedLogs.length === 0 ? (
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="log-empty">
                                                <i className="fas fa-inbox"></i>
                                                <p>Kayıt bulunamadı</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedLogs.map((log) => (
                                        <tr className="log-row" key={log.logID}>
                                            <td>{formatDate(log.islemTarihi)}</td>
                                            <td>
                                                {log.kullanici?.adSoyad ? (
                                                    <div className="log-user-cell">
                                                        <div className="log-user-avatar">{getUserInitials(log.kullanici.adSoyad)}</div>
                                                        <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{log.kullanici.adSoyad}</span>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Bilinmeyen</span>
                                                )}
                                            </td>
                                            <td><span className={`log-badge ${getIslemBadgeClass(log.islemTipi)}`}>{log.islemTipi || '-'}</span></td>
                                            <td><span className="badge-endpoint" title={log.endpoint || ''}>{getEndpointLabel(log.endpoint)}</span></td>
                                            <td>{log.takipNo || <span style={{ color: '#cbd5e1' }}>-</span>}</td>
                                            <td><span className="badge-endpoint">{log.ipAdresi || '-'}</span></td>
                                            <td>
                                                {log.basariliMi ? (
                                                    <span className="badge-success-status">Başarılı</span>
                                                ) : (
                                                    <span className="badge-fail-status">Hatalı</span>
                                                )}
                                            </td>
                                            <td>
                                                <button className="log-detail-btn" onClick={() => showLogDetail(log)}>
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
                {/* Pagination */}
                <div className="log-pagination">
                    <div className="log-pagination-left">
                        <span>{filteredLogs.length} kayıt</span>
                        <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span>kayıt/sayfa</span>
                    </div>
                    <div className="log-pagination-right">
                        <button className="log-page-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
                        </button>
                        <span className="log-page-info">Sayfa {currentPage} / {totalPages || 1}</span>
                        <button className="log-page-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14"><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
                        </button>
                    </div>
                </div>
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && selectedLog && (
                <div className="log-modal-overlay" onClick={closeDetailModal}>
                    <div className="log-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="log-modal-header">
                            <h5>İşlem Detayı — #{selectedLog.logID}</h5>
                            <button className="log-modal-close" onClick={closeDetailModal}>&times;</button>
                        </div>
                        <div className="log-modal-body">
                            <div className="log-detail-grid">
                                <div className="log-detail-section">
                                    <h6>Genel Bilgiler</h6>
                                    <table className="log-detail-table">
                                        <tbody>
                                            <tr><td className="dt-label">Tarih</td><td className="dt-value">{formatDate(selectedLog.islemTarihi)}</td></tr>
                                            <tr><td className="dt-label">{t('label.kullanici')}</td><td className="dt-value">{selectedLog.kullanici?.adSoyad ? (
                                                <div className="log-user-cell">
                                                    <div className="log-user-avatar">{getUserInitials(selectedLog.kullanici.adSoyad)}</div>
                                                    <span style={{ fontWeight: 500, color: '#1a1a1a' }}>{selectedLog.kullanici.adSoyad}</span>
                                                </div>
                                            ) : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Bilinmeyen</span>}</td></tr>
                                            <tr><td className="dt-label">IP Adresi</td><td className="dt-value">{selectedLog.ipAdresi || '-'}</td></tr>
                                            <tr>
                                                <td className="dt-label">İşlem Tipi</td>
                                                <td className="dt-value"><span className={`log-badge ${getIslemBadgeClass(selectedLog.islemTipi)}`}>{selectedLog.islemTipi || '-'}</span></td>
                                            </tr>
                                            <tr><td className="dt-label">Detay</td><td className="dt-value"><span className="badge-endpoint" title={selectedLog.endpoint || ''}>{getEndpointLabel(selectedLog.endpoint)}</span></td></tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="log-detail-section">
                                    <h6>İşlem Bilgileri</h6>
                                    <table className="log-detail-table">
                                        <tbody>
                                            <tr><td className="dt-label">Takip No</td><td className="dt-value">{selectedLog.takipNo || '-'}</td></tr>
                                            <tr><td className="dt-label">Tablo</td><td className="dt-value">{selectedLog.tabloDegisiklik || '-'}</td></tr>
                                            <tr><td className="dt-label">Kayıt ID</td><td className="dt-value">{selectedLog.kayitID || '-'}</td></tr>
                                            <tr>
                                                <td className="dt-label">Durum</td>
                                                <td className="dt-value">
                                                    {selectedLog.basariliMi ? (
                                                        <span className="badge-success-status">Başarılı</span>
                                                    ) : (
                                                        <span className="badge-fail-status">Hatalı</span>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr><td className="dt-label" colSpan={2}>İşlem Detayı</td></tr>
                                            <tr><td colSpan={2}><pre className="log-code-block">{selectedLog.islemDetayi || '-'}</pre></td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {selectedLog.hataDetayi && (
                                <div className="log-error-block">
                                    <h6><i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>Hata Detayı</h6>
                                    <pre className="log-code-block">{selectedLog.hataDetayi}</pre>
                                </div>
                            )}

                            {selectedLog.requestData && (
                                <div className="log-data-section">
                                    <h6><i className="fas fa-arrow-up text-req"></i> Request Data</h6>
                                    <pre className="log-code-block">
                                        {(() => { try { return JSON.stringify(JSON.parse(selectedLog.requestData), null, 2); } catch { return selectedLog.requestData; } })()}
                                    </pre>
                                </div>
                            )}

                            {selectedLog.responseData && (
                                <div className="log-data-section">
                                    <h6><i className="fas fa-arrow-down text-res"></i> Response Data</h6>
                                    <pre className="log-code-block">
                                        {(() => { try { return JSON.stringify(JSON.parse(selectedLog.responseData), null, 2); } catch { return selectedLog.responseData; } })()}
                                    </pre>
                                </div>
                            )}
                        </div>
                        <div className="log-modal-footer">
                            <button className="log-btn-close" onClick={closeDetailModal}>{t('btn.kapat')}</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default IslemKayitlari;
